import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import MobileLayout from '../../components/MobileLayout';
import VideoThumbnail from '../../../components/VideoThumbnail/VideoThumbnail';
import { getBaseVideoList } from '../../../services/video';
import { useVideoGroups } from '../../../hooks/useVideoGroups';
import './MobileTasks.css';

const PAGE_SIZE = 10;
const ALL_TAB = 'all';

const STATUS_MAP = {
  4: { label: '已完成', cls: 'mtask-status-badge--success' },
  3: { label: '失败',   cls: 'mtask-status-badge--failed' },
  2: { label: '生成中', cls: 'mtask-status-badge--processing' },
  1: { label: '排队中', cls: 'mtask-status-badge--queued' },
};

const IconVideo  = () => <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="15" height="10" rx="2"/><polyline points="17 10 22 7 22 17 17 14"/></svg>;
const IconSearch = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>;
const IconPlus   = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const IconMore   = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="5" cy="12" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/></svg>;
const IconCheck  = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>;

const parseVideo = (v, i) => ({
  id:        v.id        || `vid-${i}`,
  title:     v.title     || v.name     || `视频 ${i + 1}`,
  status:    v.status,
  createdAt: v.created_at || v.createdAt || '',
  thumbnail: v.thumbnail  || v.cover_url || v.coverUrl || '',
  videoUrl:  v.video_url  || v.url       || '',
  previewUrl: v.preview_url || v.previewUrl || '',  // 预览图
  text:      v.text       || '',
});

export default function MobileTasks() {
  const { user }    = useAuth();
  const navigate    = useNavigate();
  const { groups, assignments, createGroup, renameGroup, deleteGroup, assignVideos } =
    useVideoGroups(user?.phone);

  // ── Data ──
  const [allVideos, setAllVideos]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [page, setPage]               = useState(1);
  const [totalPages, setTotalPages]   = useState(1);
  const sentinelRef = useRef(null);

  // ── Filter / search ──
  const [search, setSearch]       = useState('');
  const [activeTab, setActiveTab] = useState(ALL_TAB);

  // ── Selection (multi-select) ──
  const [selecting, setSelecting]     = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showMoveMenu, setShowMoveMenu] = useState(false);

  // ── Group management ──
  const [showNewGroup, setShowNewGroup]   = useState(false);
  const [newGroupName, setNewGroupName]   = useState('');
  const [showGroupMenu, setShowGroupMenu] = useState(false);
  const [renamingGroup, setRenamingGroup] = useState(false);
  const [renameGroupVal, setRenameGroupVal] = useState('');
  const newGroupRef  = useRef(null);
  const renameGrpRef = useRef(null);

  // ── Fetch data ──
  useEffect(() => {
    if (!user?.phone) return;
    setLoading(true);
    setError('');
    getBaseVideoList({
      phone: user.phone,
      page,
      pageSize: PAGE_SIZE,
      groupId: activeTab === ALL_TAB ? '' : activeTab,
    })
      .then((data) => {
        const list = (Array.isArray(data?.videos) ? data.videos : []).map(parseVideo);
        setAllVideos((prev) => page === 1 ? list : [...prev, ...list]);
        const total = Number(data?.total ?? data?.data?.total ?? 0);
        setTotalPages(Math.max(1, Math.ceil((total || list.length) / PAGE_SIZE)));
      })
      .catch((err) => setError(err.message || '加载失败'))
      .finally(() => setLoading(false));
  }, [user?.phone, page, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tab/search 只是客户端过滤，不需要重新请求，只清选择状态
  useEffect(() => {
    setSelectedIds([]);
    setSelecting(false);
  }, [activeTab, search]);

  // IntersectionObserver
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !loading && page < totalPages) setPage((p) => p + 1);
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loading, page, totalPages]);

  // Focus helpers
  useEffect(() => { if (showNewGroup) newGroupRef.current?.focus(); }, [showNewGroup]);
  useEffect(() => { if (renamingGroup) renameGrpRef.current?.focus(); }, [renamingGroup]);

  // ── Filtered list ──
  // Backend now handles group filtering, so we only need search filtering.
  const filteredVideos = useMemo(() => {
    let list = allVideos;
    // Only apply search filter, group filtering is done by backend
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((v) => v.title.toLowerCase().includes(q));
    }
    return list;
  }, [allVideos, search]);

  // ── Group operations ──
  const handleCreateGroup = useCallback(async () => {
    const name = newGroupName.trim();
    if (!name) return;
    if (groups.some((g) => g.name === name)) {
      alert(`分组「${name}」已存在`);
      return;
    }
    try {
      const id = await createGroup(name);
      setActiveTab(id);
      setNewGroupName('');
      setShowNewGroup(false);
    } catch (error) {
      alert(`创建分组失败: ${error.message || '请重试'}`);
    }
  }, [newGroupName, createGroup, groups]);

  const handleConfirmRenameGroup = useCallback(() => {
    const name = renameGroupVal.trim();
    if (!name) { setRenamingGroup(false); return; }
    if (groups.some((g) => g.id !== activeTab && g.name === name)) {
      alert(`分组「${name}」已存在`);
      return;
    }
    renameGroup(activeTab, name);
    setRenamingGroup(false);
    setShowGroupMenu(false);
  }, [renameGroupVal, activeTab, renameGroup, groups]);

  const handleDeleteGroup = useCallback(() => {
    const g = groups.find((g) => g.id === activeTab);
    if (!g) return;
    if (!window.confirm(`确认删除分组「${g.name}」？\n该分组的视频将归回"全部"。`)) return;
    deleteGroup(g.id);
    setActiveTab(ALL_TAB);
    setShowGroupMenu(false);
  }, [groups, activeTab, deleteGroup]);

  // ── Selection ──
  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }, []);

  const handleMoveToGroup = useCallback(async (groupId) => {
    try {
      await assignVideos(selectedIds, groupId || null);
      setSelectedIds([]);
      setSelecting(false);
      setShowMoveMenu(false);
    } catch (error) {
      alert(`分配失败: ${error.message || '请重试'}`);
    }
  }, [selectedIds, assignVideos]);

  const exitSelecting = () => { setSelecting(false); setSelectedIds([]); setShowMoveMenu(false); };

  // ── Download ──
  const handleDownload = (e, videoUrl) => {
    e.stopPropagation();
    if (videoUrl) window.open(videoUrl, '_blank');
  };

  // ── Card tap ──
  const handleCardTap = (v) => {
    if (selecting) {
      toggleSelect(v.id);
    } else {
      navigate(`/m/tasks/${v.id}`, { state: { video: v } });
    }
  };

  const activeGroupName = groups.find((g) => g.id === activeTab)?.name;
  const moveTargets = groups.filter((g) => g.id !== activeTab);

  return (
    <MobileLayout title="我的视频">
      <div className="mtask-root">

        {/* ── Search bar ── */}
        <div className="mtask-search-wrap">
          <span className="mtask-search-icon"><IconSearch /></span>
          <input
            className="mtask-search"
            type="search"
            placeholder="搜索视频名称..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {!selecting && (
            <button className="mtask-multisel-btn" onClick={() => setSelecting(true)}>多选</button>
          )}
        </div>

        {/* ── Group tabs ── */}
        <div className="mtask-tabs-wrap">
          <div className="mtask-tabs">
            <button
              className={`mtask-tab${activeTab === ALL_TAB ? ' active' : ''}`}
              onClick={() => { setActiveTab(ALL_TAB); setPage(1); setShowGroupMenu(false); }}
            >全部</button>

            {groups.map((g) => (
              <button
                key={g.id}
                className={`mtask-tab${activeTab === g.id ? ' active' : ''}`}
                onClick={() => { setActiveTab(g.id); setPage(1); setShowGroupMenu(false); }}
              >
                {g.name}
                {activeTab === g.id && (
                  <span
                    className="mtask-tab-more"
                    onClick={(e) => { e.stopPropagation(); setShowGroupMenu((p) => !p); setRenamingGroup(false); }}
                  ><IconMore /></span>
                )}
              </button>
            ))}

            {showNewGroup ? (
              <div className="mtask-new-group-inline">
                <input
                  ref={newGroupRef}
                  className="mtask-new-group-input"
                  placeholder="分组名"
                  maxLength={12}
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreateGroup();
                    if (e.key === 'Escape') { setShowNewGroup(false); setNewGroupName(''); }
                  }}
                />
                <button className="mtask-new-group-ok" onClick={handleCreateGroup}>确认</button>
                <button className="mtask-new-group-cancel" onClick={() => { setShowNewGroup(false); setNewGroupName(''); }}>×</button>
              </div>
            ) : (
              <button className="mtask-tab mtask-tab-add" onClick={() => setShowNewGroup(true)}>
                <IconPlus />
              </button>
            )}
          </div>

          {/* Group "..." dropdown */}
          {showGroupMenu && activeTab !== ALL_TAB && (
            <>
              <div className="mtask-group-menu-backdrop" onClick={() => { setShowGroupMenu(false); setRenamingGroup(false); }} />
              <div className="mtask-group-menu">
                {renamingGroup ? (
                  <div className="mtask-group-rename-row">
                    <input
                      ref={renameGrpRef}
                      className="mtask-group-rename-input"
                      value={renameGroupVal}
                      maxLength={12}
                      onChange={(e) => setRenameGroupVal(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleConfirmRenameGroup();
                        if (e.key === 'Escape') setRenamingGroup(false);
                      }}
                    />
                    <button className="mtask-group-rename-ok" onClick={handleConfirmRenameGroup}>确认</button>
                  </div>
                ) : (
                  <>
                    <button className="mtask-group-menu-item" onClick={() => { setRenamingGroup(true); setRenameGroupVal(activeGroupName || ''); }}>重命名</button>
                    <button className="mtask-group-menu-item danger" onClick={handleDeleteGroup}>删除分组</button>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {/* ── Content ── */}
        {loading && page === 1 ? (
          <div className="mtask-loading"><div className="mtask-spinner" /></div>
        ) : error ? (
          <div className="mtask-error">{error}</div>
        ) : filteredVideos.length === 0 && !loading ? (
          <div className="mtask-empty">
            <span className="mtask-empty-icon"><IconVideo /></span>
            <span>{search ? '没有匹配的视频' : activeTab !== ALL_TAB ? '该分组暂无视频' : '还没有视频任务'}</span>
            {activeTab === ALL_TAB && !search && (
              <Link to="/m/generate" className="mtask-empty-cta">去生成第一个视频</Link>
            )}
          </div>
        ) : (
          <>
            <div className="mtask-grid">
              {filteredVideos.map((v) => {
                const pill       = STATUS_MAP[v.status] || STATUS_MAP[1];
                const isSelected = selectedIds.includes(v.id);
                const groupId    = assignments[String(v.id)];
                const groupName  = activeTab === ALL_TAB && groupId
                  ? groups.find((g) => g.id === groupId)?.name
                  : null;

                return (
                  <div
                    key={v.id}
                    className={`mtask-card${isSelected ? ' selected' : ''}`}
                    onClick={() => handleCardTap(v)}
                  >
                    <div className="mtask-thumb">
                      {v.thumbnail || v.previewUrl || v.videoUrl ? (
                        <VideoThumbnail
                          previewUrl={v.previewUrl || v.thumbnail}
                          videoUrl={v.videoUrl}
                          title={v.title}
                          className="mtask-thumb-media"
                        />
                      ) : (
                        <span className="mtask-thumb-icon"><IconVideo /></span>
                      )}
                      <span className={`mtask-status-badge ${pill.cls}`}>{pill.label}</span>
                      {groupName && <span className="mtask-group-badge">{groupName}</span>}
                      {v.status === 4 && v.videoUrl && !selecting && (
                        <button className="mtask-dl-btn" onClick={(e) => handleDownload(e, v.videoUrl)}>下载</button>
                      )}
                      {selecting && (
                        <div className={`mtask-check-circle${isSelected ? ' checked' : ''}`}>
                          {isSelected && <IconCheck />}
                        </div>
                      )}
                    </div>
                    <div className="mtask-body">
                      <div className="mtask-title">{v.title}</div>
                      {v.createdAt && <div className="mtask-meta">{v.createdAt}</div>}
                    </div>
                  </div>
                );
              })}
            </div>

            <div ref={sentinelRef} className="mtask-sentinel" />
            {loading && page > 1 && (
              <div className="mtask-bottom-loading"><div className="mtask-spinner" /></div>
            )}
            {!loading && totalPages > 0 && page >= totalPages && filteredVideos.length > 0 && (
              <div className="mtask-all-loaded">已加载全部视频</div>
            )}
          </>
        )}

        {/* ── Batch action bar ── */}
        {selecting && (
          <div className="mtask-batch-bar">
            <span className="mtask-batch-info">
              {selectedIds.length > 0 ? `已选 ${selectedIds.length} 个` : '点击视频选择'}
            </span>

            {selectedIds.length > 0 && (
              <div className="mtask-move-wrap">
                {showMoveMenu && (
                  <div className="mtask-move-backdrop" onClick={() => setShowMoveMenu(false)} />
                )}
                <button className="mtask-move-btn" onClick={() => setShowMoveMenu((p) => !p)}>
                  移至分组 ▾
                </button>
                {showMoveMenu && (
                  <div className="mtask-move-menu">
                    {moveTargets.length === 0 ? (
                      <div className="mtask-move-item disabled">暂无分组，请先新建</div>
                    ) : (
                      moveTargets.map((g) => (
                        <button key={g.id} className="mtask-move-item" onClick={() => handleMoveToGroup(g.id)}>{g.name}</button>
                      ))
                    )}
                    {activeTab !== ALL_TAB && (
                      <button className="mtask-move-item ungrouped" onClick={() => handleMoveToGroup(null)}>移出分组</button>
                    )}
                  </div>
                )}
              </div>
            )}

            <button className="mtask-batch-cancel" onClick={exitSelecting}>取消</button>
          </div>
        )}

      </div>
    </MobileLayout>
  );
}
